import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/lib/api'
import { ArrowLeft } from 'lucide-react'

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export default function CreateTenantPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)

  const createTenant = useMutation({
    mutationFn: adminApi.createTenant,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
      navigate(`/tenants/${data.tenant_id}`)
    },
  })

  function handleNameChange(value: string) {
    setName(value)
    if (!slugManuallyEdited) {
      setSlug(slugify(value))
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    createTenant.mutate({
      name,
      slug,
      description: description || undefined,
      owner_email: ownerEmail || undefined,
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/tenants"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Kembali
        </Link>
      </div>

      <div>
        <h1 className="text-2xl font-bold">Buat Tenant Baru</h1>
        <p className="text-sm text-muted-foreground">
          Tenant akan otomatis diberikan plan Free dan default theme.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-lg space-y-5">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Nama Grup <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="contoh: Sakura Idol"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Slug (Subdomain) <span className="text-destructive">*</span>
          </label>
          <div className="flex items-center">
            <input
              type="text"
              required
              value={slug}
              onChange={(e) => {
                setSlugManuallyEdited(true)
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
              }}
              className="w-full rounded-l-md border border-r-0 border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="sakura-idol"
            />
            <span className="rounded-r-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
              .oneidol.id
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Hanya huruf kecil, angka, dan tanda hubung.
          </p>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Deskripsi</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Deskripsi singkat tentang grup idol ini..."
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Email Owner</label>
          <input
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="owner@example.com"
          />
          <p className="text-xs text-muted-foreground">
            Jika diisi, akun akan dibuat/dihubungkan ke email ini sebagai owner tenant.
          </p>
        </div>

        {createTenant.error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {createTenant.error.message}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={createTenant.isPending || !name || !slug}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createTenant.isPending ? 'Membuat...' : 'Buat Tenant'}
          </button>
          <Link
            to="/tenants"
            className="rounded-md border border-input px-4 py-2 text-sm hover:bg-muted"
          >
            Batal
          </Link>
        </div>
      </form>
    </div>
  )
}
